import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabaseClient';
import useSEO from '../hooks/useSEO';

const { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheckCircle, FiExternalLink, FiAlertTriangle, FiSmartphone } = FiIcons;

const Contact = () => {
  // Apply Dynamic SEO
  useSEO({
    title: 'Contact Ramirez Mobile | Request CARB Compliance Quote',
    description: 'Contact Ramirez Mobile Clean Truck for official CARB Clean Truck Checks and diesel emissions testing in Ontario, CA. Same-day mobile service available.',
    canonicalRoute: '/contact'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [adminEmail, setAdminEmail] = useState('cleanairtruckcheck@gmail.com');

  // Fetch admin email settings on mount
  useEffect(() => {
    const settings = localStorage.getItem('ramirez_settings');
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        if (parsed.email) setAdminEmail(parsed.email);
      } catch (e) {
        console.error("Error loading settings", e);
      }
    }

    // Inject ContactPage Schema for Google Knowledge Graph
    const scriptId = 'contact-schema-json-ld';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Ramirez Mobile Clean Truck",
        "description": "Get in touch for CARB compliance and mobile diesel emissions testing.",
        "url": "https://ramirezmobilecleantruck.com/contact",
        "mainEntity": {
          "@type": "LocalBusiness",
          "name": "Ramirez Mobile Clean Truck",
          "telephone": "+19092842738",
          "email": "cleanairtruckcheck@gmail.com",
          "areaServed": "Ontario, CA and Inland Empire",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Ontario",
            "addressRegion": "CA",
            "addressCountry": "US"
          }
        }
      });
      document.head.appendChild(script);
    }
    return () => {
      const script = document.getElementById(scriptId);
      if (script) document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceSelect = (selectedService) => {
    setFormData(prev => ({ ...prev, service: selectedService }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    if (!formData.name || !formData.phone || !formData.service) {
      setSubmitError("Please fill out all required fields.");
      setIsSubmitting(false);
      return;
    }

    const newLead = {
      id: Date.now(),
      name: formData.name,
      company: formData.company,
      email: formData.email,
      phone: formData.phone,
      type: formData.service || 'Quote Request',
      status: 'New',
      date: new Date().toLocaleDateString(),
      message: formData.message
    };

    try {
      const existingLeads = JSON.parse(localStorage.getItem('ramirez_leads') || '[]');
      const updatedLeads = [newLead, ...existingLeads];
      localStorage.setItem('ramirez_leads', JSON.stringify(updatedLeads));

      if (supabase) {
        const { error } = await supabase
          .from('leads')
          .insert([
            {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              company: formData.company,
              type: 'Quote Request',
              status: 'New',
              message: `Service Needed: ${formData.service}\n\nDetails: ${formData.message}`
            }
          ]);
        if (error) {
          console.error("Supabase Error:", error);
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission Error:', error);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEmailApp = () => {
    const subject = `New Quote Request: ${formData.company || formData.name}`;
    const body = `Name: ${formData.name}%0D%0ACompany: ${formData.company}%0D%0APhone: ${formData.phone}%0D%0AEmail: ${formData.email}%0D%0AService Needed: ${formData.service}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
    window.location.href = `mailto:${adminEmail}?subject=${subject}&body=${body}`;
  };

  const handleOpenTextApp = () => {
    const phoneNumber = "19092842738";
    const body = `New Quote Request:%0AName: ${formData.name}%0ACompany: ${formData.company}%0APhone: ${formData.phone}%0AService: ${formData.service}%0A${formData.message}`;
    window.location.href = `sms:${phoneNumber}?body=${body}`;
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
  };

  const contactInfo = [
    { icon: FiPhone, title: "Call Us", details: "(909) 284-2738", subtitle: "Emergency Service Available", link: "tel:9092842738" },
    { icon: FiMapPin, title: "Service Area", details: "Ontario, CA & Inland Empire", subtitle: "Mobile service throughout Southern California", link: "https://share.google/3y2ABFaMxbdz6hzaZ", isExternal: true },
    { icon: FiClock, title: "Business Hours", details: "Monday - Saturday: 7AM - 6PM", subtitle: "Emergency service available", link: null }
  ];

  const services = [
    "CARB Compliance Testing",
    "Opacity Testing",
    "Emergency Service",
    "Fleet Management",
    "Other"
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-blue-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/a728ddab7d71aca898b8aa1e2ed2cb3f.jpeg" 
            alt="Contact us support" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Contact Us
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl mb-8"
            >
              Get Your Free CARB Compliance Quote Today
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-blue-100"
            >
              Ready to ensure your diesel trucks meet California's emissions standards? Contact our CARB compliance experts in Ontario, CA.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-red-600 text-white py-4 shadow-md relative z-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-lg font-semibold flex items-center justify-center gap-2">
              <SafeIcon icon={FiAlertTriangle} /> 
              EMERGENCY SERVICE AVAILABLE - Call <a href="tel:9092842738" className="underline hover:text-red-100 ml-1">(909) 284-2738</a> for Immediate Assistance
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Get In Touch</h2>
            <h3 className="text-2xl text-gray-600">Multiple Ways to Reach Our CARB Compliance Experts</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-shadow border border-gray-100 flex flex-col items-center"
              >
                <SafeIcon icon={info.icon} className="text-4xl text-blue-600 mb-4" />
                <h4 className="text-xl font-semibold text-gray-800 mb-2">{info.title}</h4>
                {info.link ? (
                  <a 
                    href={info.link} 
                    target={info.isExternal ? "_blank" : "_self"}
                    rel={info.isExternal ? "noopener noreferrer" : ""}
                    className="text-lg text-blue-600 font-semibold mb-2 hover:underline flex items-center gap-2"
                  >
                    {info.details}
                    {info.isExternal && <SafeIcon icon={FiExternalLink} className="text-sm" />}
                  </a>
                ) : (
                  <p className="text-lg text-blue-600 font-semibold mb-2">{info.details}</p>
                )}
                <p className="text-gray-600">{info.subtitle}</p>
              </motion.div>
            ))}
          </div>

          {/* Quick Call Section */}
          <div className="text-center mb-16">
            <div className="bg-blue-50 p-8 rounded-lg max-w-2xl mx-auto border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Need Service Today?</h3>
              <p className="text-gray-600 mb-6">
                For immediate CARB compliance assistance, emergency service, or urgent questions, call our direct line now.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:9092842738" className="bg-blue-600 text-white font-bold px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors text-xl inline-flex items-center justify-center space-x-3 shadow-lg">
                  <SafeIcon icon={FiPhone} />
                  <span>Call (909) 284-2738</span>
                </a>
                <a href="https://share.google/3y2ABFaMxbdz6hzaZ" target="_blank" rel="noopener noreferrer" className="bg-white text-blue-600 border-2 border-blue-600 font-bold px-8 py-4 rounded-lg hover:bg-blue-50 transition-colors text-xl inline-flex items-center justify-center space-x-3 shadow-lg">
                  <SafeIcon icon={FiMapPin} />
                  <span>View on Maps</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Request a Free Quote</h2>
              <h3 className="text-2xl text-gray-600">Tell Us About Your CARB Compliance Needs</h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white p-8 rounded-lg shadow-md">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-gray-700 font-semibold mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="Your company name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Service Needed *
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {services.map((service, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleServiceSelect(service)}
                            className={`py-3 px-4 rounded-lg border text-left transition-all flex items-center justify-between group ${
                              formData.service === service 
                                ? 'bg-blue-600 border-blue-600 text-white shadow-md' 
                                : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                            }`}
                          >
                            <span className="text-sm font-medium">{service}</span>
                            {formData.service === service && <SafeIcon icon={FiCheckCircle} className="text-white" />}
                          </button>
                        ))}
                      </div>
                      <input type="hidden" name="service" value={formData.service} />
                      {!formData.service && (
                        <p className="text-xs text-gray-500 mt-2">Please select a service option above.</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                        Additional Details
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="4"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        placeholder="Tell us about your truck(s), timeline, or any specific requirements..."
                      ></textarea>
                    </div>

                    {submitError && (
                      <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2">
                        <SafeIcon icon={FiAlertTriangle} />
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.service || !formData.phone || !formData.name}
                      className={`w-full bg-blue-600 text-white font-bold py-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 shadow-md ${
                        (isSubmitting || !formData.service || !formData.phone || !formData.name) ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? (
                        <span>Processing...</span>
                      ) : (
                        <>
                          <SafeIcon icon={FiSend} />
                          <span>Send Request</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-center text-gray-500 mt-2">
                      Your information is secure and will only be used to contact you.
                    </p>
                  </form>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-5xl text-green-500 mb-4 mx-auto block" />
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Request Processed</h3>
                    <p className="text-gray-600 mb-6">
                      We have logged your request. For fastest service, please send your details to our dispatch team using one of the buttons below.
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <button 
                        onClick={handleOpenTextApp}
                        className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-bold flex items-center justify-center gap-2 shadow-lg"
                      >
                        <SafeIcon icon={FiSmartphone} />
                        Send via Text to (909) 284-2738
                      </button>
                      <button 
                        onClick={handleOpenEmailApp}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-bold flex items-center justify-center gap-2 shadow-lg"
                      >
                        <SafeIcon icon={FiMail} />
                        Send via Email App
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 mb-4">
                      Email: <span className="font-semibold text-gray-700">{adminEmail}</span>
                    </p>
                    
                    <button 
                      onClick={handleReset}
                      className="mt-4 text-blue-600 hover:underline text-sm"
                    >
                      Submit another request
                    </button>
                  </motion.div>
                )}
              </div>

              {/* Service Areas & Info */}
              <div className="space-y-8">
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Service Areas</h3>
                  <p className="text-gray-600 mb-4">
                    We provide mobile CARB compliance services throughout Southern California:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <div>• Ontario, CA</div>
                    <div>• Rancho Cucamonga</div>
                    <div>• Upland, CA</div>
                    <div>• Fontana, CA</div>
                    <div>• Chino, CA</div>
                    <div>• Pomona, CA</div>
                    <div>• San Bernardino</div>
                    <div>• Riverside, CA</div>
                  </div>
                  <div className="mt-6">
                    <a href="https://share.google/3y2ABFaMxbdz6hzaZ" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline flex items-center gap-2">
                      <SafeIcon icon={FiMapPin} /> View Service Area Map
                    </a>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">What to Expect</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                      <span className="text-gray-700">Response within 1 hour during business hours</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                      <span className="text-gray-700">Free, detailed quote for your specific needs</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                      <span className="text-gray-700">Flexible scheduling to minimize downtime</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                      <span className="text-gray-700">Professional, certified technicians</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;